import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, Download, Eye, Pencil, Plus, Loader2, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InvoiceViewDialog } from "@/components/invoiceGeneration/InvoiceViewDialog";
import { downloadInvoicePdf } from "@/components/invoiceGeneration/invoicePdfDownload";
import { useTranslations } from "use-intl";
import { useInvoiceStatusLabel } from "@/i18n/useInvoiceStatusLabel";
import { invoiceProvider, formatMoneyForCurrency } from "@/services/invoiceGeneration";
import type { Receivable } from "@/services/invoiceGeneration";
import { userFacingErrorMessage } from '@/utils/userFacingError';

/** Which row action, if any, is currently confirming. */
type PendingConfirm = { kind: "send" | "delete"; invoice: Receivable } | null;

/**
 * The outcome of the last row action, rendered inline on the page.
 *
 * Deliberately NOT a toast, and still not one now that toasts render (FIN-459): a
 * refused mark-as-sent or delete is something the user has to act on, so it belongs on
 * the row it refused rather than in a notification that fades. Same inline-Alert shape
 * CreateInvoice.tsx uses for its own issue failures.
 */
type RowFeedback = { tone: "success" | "error"; title: string; description: string } | null;

/**
 * EC7 wants a blocked mark-as-sent reported as MISSING FIELDS — the thing the user can
 * act on — not as a failed transition. The backend answers with a generic message plus a
 * `missing` list in `details`; showing only the message told the user something was
 * missing without ever saying what, which is not actionable.
 */
function describeMarkSentFailure(err: unknown, unknownError: string): string {
  const missing = (err as { details?: { missing?: string[] } })?.details?.missing;
  if (Array.isArray(missing) && missing.length > 0) return missing.join(" · ");
  return userFacingErrorMessage(err, unknownError);
}

export default function Invoices() {
  const t = useTranslations("consultantReceivables.invoices");
  const tShared = useTranslations("common.actions");
  const tPayables = useTranslations("consultantPayables.actions");
  const statusLabel = useInvoiceStatusLabel();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Receivable[]>([]);
  const [viewing, setViewing] = useState<Receivable | null>(null);
  const [confirming, setConfirming] = useState<PendingConfirm>(null);
  const [feedback, setFeedback] = useState<RowFeedback>(null);
  // Keyed by invoice id, not a single boolean: the actions live in list rows, so a
  // shared flag would disable every row's buttons while one is in flight (EC4).
  const [busyId, setBusyId] = useState<string | null>(null);
  // A Mark as sent started from the View dialog reports its refusal there (EC9).
  const [viewMarkSentError, setViewMarkSentError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function download(invoice: Receivable) {
    setDownloadingId(invoice.id);
    try {
      await downloadInvoicePdf(invoice);
    } catch {
      setFeedback({
        tone: "error",
        title: t("feedback.downloadFailedTitle"),
        description: t("feedback.downloadFailedBody", { number: invoice.number }),
      });
    } finally {
      setDownloadingId(null);
    }
  }

  async function refresh() {
    const inv = await invoiceProvider.listReceivables();
    setInvoices(inv);
  }

  useEffect(() => {
    let alive = true;
    invoiceProvider.listReceivables().then((inv) => {
      if (alive) { setInvoices(inv); setLoading(false); }
    });
    return () => { alive = false; };
  }, []);

  async function markAsSent(invoice: Receivable) {
    setBusyId(invoice.id);
    setFeedback(null);
    try {
      const sent = await invoiceProvider.markAsSent(invoice.id);
      setViewing(null);
      setFeedback({
        tone: "success",
        title: t("feedback.sentTitle", { number: sent.number }),
        description: t("feedback.sentBody", { amount: formatMoneyForCurrency(sent.toPay, sent.currency) }),
      });
      await refresh();
    } catch (err) {
      // A refusal here is expected and actionable — a closed accounting period, a missing
      // mandatory field, or a concurrent delete. The invoice is still a draft in every
      // one of those cases, so the message must be shown rather than swallowed.
      const failure = describeMarkSentFailure(err, t("feedback.unknownError"));
      if (viewing?.id === invoice.id) setViewMarkSentError(failure);
      else setFeedback({
        tone: "error",
        title: t("feedback.markSentFailedTitle"),
        description: failure,
      });
      await refresh();
    } finally {
      setBusyId(null);
      setConfirming(null);
    }
  }

  async function deleteDraft(invoice: Receivable) {
    setBusyId(invoice.id);
    setFeedback(null);
    try {
      await invoiceProvider.deleteDraft(invoice.id);
      setFeedback({
        tone: "success",
        title: t("feedback.draftDeletedTitle"),
        description: t("feedback.draftDeletedBody"),
      });
      await refresh();
    } catch (err) {
      setFeedback({
        tone: "error",
        title: t("feedback.deleteFailedTitle"),
        description: userFacingErrorMessage(err, t("feedback.unknownError")),
      });
      await refresh();
    } finally {
      setBusyId(null);
      setConfirming(null);
    }
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("title")}</h1>
          <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
        </div>
        <Button onClick={() => navigate("/create-invoice")}>
          <Plus size={16} className="mr-1.5" /> {t("create")}
        </Button>
      </div>

      {/* The one place a row action's outcome is reported. `role="alert"` for a refusal
          (assertive — the user asked for something and did not get it) and `role="status"`
          for a confirmation, so a test or a screen reader can tell the two apart without
          matching on copy. */}
      {feedback && (
        <Alert
          variant={feedback.tone === "error" ? "destructive" : "default"}
          role={feedback.tone === "error" ? "alert" : "status"}
          className="mb-4"
        >
          {feedback.tone === "error"
            ? <AlertCircle className="h-4 w-4" />
            : <CheckCircle2 className="h-4 w-4" />}
          <AlertTitle>{feedback.title}</AlertTitle>
          <AlertDescription>{feedback.description}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 animate-spin" /> {t("loading")}
        </div>
      ) : invoices.length === 0 ? (
        <div className="rounded-lg border bg-card p-10 text-center">
          <p className="text-muted-foreground">{t("empty")}</p>
          <Button className="mt-4" onClick={() => navigate("/create-invoice")}>
            <Plus size={16} className="mr-1.5" /> {t("createFirst")}
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
                <th className="px-4 py-2.5 text-left font-medium">{t("columns.number")}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t("columns.customer")}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t("columns.date")}</th>
                <th className="px-4 py-2.5 text-left font-medium">{t("columns.status")}</th>
                <th className="px-4 py-2.5 text-right font-medium">{t("columns.total")}</th>
                <th className="px-4 py-2.5 text-right font-medium" />
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const isDraft = inv.status === "draft";
                const busy = busyId === inv.id;
                return (
                  <tr key={inv.id} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-3 font-medium">{inv.number}</td>
                    <td className="px-4 py-3">{inv.customerName}</td>
                    <td className="px-4 py-3 text-muted-foreground">{inv.invoiceDate}</td>
                    <td className="px-4 py-3">
                      <Badge variant={isDraft ? "outline" : "secondary"}>{statusLabel(inv.status)}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{formatMoneyForCurrency(inv.toPay, inv.currency)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setViewMarkSentError(null); setViewing(inv); }}>
                          <Eye size={15} className="mr-1" /> {t("view")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={tPayables("downloadPdf")}
                          disabled={downloadingId === inv.id}
                          onClick={() => void download(inv)}
                        >
                          {downloadingId === inv.id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                        </Button>
                        {/* Neither Edit, Mark as sent nor Delete is rendered for a sent invoice
                            (EC5/AC7) — the backend refuses all three regardless. */}
                        {isDraft && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busy}
                              onClick={() => navigate(`/create-invoice?draft=${inv.id}`)}
                            >
                              <Pencil size={15} className="mr-1" /> {t("edit")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busy}
                              onClick={() => setConfirming({ kind: "send", invoice: inv })}
                            >
                              {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Send size={15} className="mr-1" />}
                              {t("markAsSent")}
                            </Button>
                            <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={t("deleteLabel")}
                              className="text-destructive hover:text-destructive"
                              disabled={busy}
                              onClick={() => setConfirming({ kind: "delete", invoice: inv })}
                            >
                              <Trash2 size={15} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={confirming !== null} onOpenChange={(open) => { if (!open) setConfirming(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming?.kind === "send" ? t("confirm.sendTitle") : t("confirm.deleteTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming?.kind === "send" ? t("confirm.sendBody") : t("confirm.deleteBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tShared("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              // Native disabled, not CSS dimming — a second click must not fire a second
              // request while the first is still in flight (EC4).
              disabled={busyId !== null}
              onClick={(e) => {
                // The dialog closes on action by default; keep it open so the pending
                // state is actually visible for the duration of the request.
                e.preventDefault();
                if (!confirming) return;
                if (confirming.kind === "send") void markAsSent(confirming.invoice);
                else void deleteDraft(confirming.invoice);
              }}
            >
              {busyId !== null ? <Loader2 size={15} className="mr-1 animate-spin" /> : null}
              {confirming?.kind === "send" ? t("markAsSent") : t("deleteLabel")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <InvoiceViewDialog
        invoice={viewing}
        onClose={() => { setViewing(null); setViewMarkSentError(null); }}
        onMarkAsSent={(inv) => { setViewMarkSentError(null); setConfirming({ kind: "send", invoice: inv }); }}
        markSentError={viewMarkSentError}
        busy={busyId !== null}
      />
    </div>
  );
}
